import type { SessionDto } from '@pos/api-client';
import { initI18n, type Language } from '@pos/i18n';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface SessionState {
  session?: SessionDto;
  start(session: SessionDto): void;
  renew(session: SessionDto): void;
  end(): void;
}

/**
 * The logged-in admin, kept in the tab's sessionStorage: a reload keeps the session, closing the tab ends it, and an
 * expired token is discarded on load. One store per portal, under its own key.
 */
export function createSessionStore(storageKey: string) {
  return create<SessionState>()(
    persist(
      (set) => ({
        session: undefined,
        start: (session) => {
          initI18n(session.staff.preferredLanguage as Language);
          set({ session });
        },
        renew: (session) => set({ session }),
        end: () => set({ session: undefined }),
      }),
      {
        name: storageKey,
        storage: createJSONStorage(() => sessionStorage),
        partialize: ({ session }) => ({ session }),
        merge: (persisted, current) => {
          const saved = (persisted as Partial<SessionState> | undefined)?.session;
          if (!saved || new Date(saved.expiresAt).getTime() <= Date.now()) return { ...current, session: undefined };
          initI18n(saved.staff.preferredLanguage as Language);
          return { ...current, session: saved };
        },
      },
    ),
  );
}

export type SessionStore = ReturnType<typeof createSessionStore>;

/**
 * Whether the session may do it, as the server decides (ADR 0116, `PermissionGrant`): the code itself; for a feature's `read` or
 * `manage` also its `manage` (for a read), and the same on an enclosing area of at least two parts (`ho.catalog.manage` allows every
 * `ho.catalog.*` feature). Other actions need their own code. `within('ho.catalog')` takes any permission of the area, and
 * `a|b` either one, as `RequireAnyPermission` does.
 */
export function allows(held: readonly string[], permission: string): boolean {
  if (permission.includes('|')) return permission.split('|').some((p) => allows(held, p));
  if (permission.endsWith('.*')) return held.some((code) => code.startsWith(permission.slice(0, -1)));
  if (held.includes(permission)) return true;
  const dot = permission.lastIndexOf('.');
  const action = permission.slice(dot + 1);
  if (dot < 0 || (action !== 'read' && action !== 'manage')) return false;
  for (let prefix = permission.slice(0, dot); prefix.includes('.'); prefix = prefix.slice(0, prefix.lastIndexOf('.'))) {
    if (held.includes(`${prefix}.manage`) || (action === 'read' && held.includes(`${prefix}.read`))) return true;
  }
  return false;
}

/** A permission any code of the area meets, e.g. for a lookup every screen of the area uses (`PermissionGrant.Within`). */
export const within = (area: string) => `${area}.*`;

export function hasPermission(session: SessionDto | undefined, permission: string | undefined) {
  return !permission || allows(session?.permissions ?? [], permission);
}
